'use strict';

const libWorkspace = require('pict-section-workspace');
const libLogoSlotController = require('./LogoSlotController.js');
const libLabelManagerController = require('./LabelManagerController.js');
const libLabelBehaviorController = require('./LabelBehaviorController.js');
const libProjectObservationLabelController = require('./ProjectObservationLabelController.js');
const libRecordSetAssociationEditor = require('pict-section-recordset/source/views/associate/RecordSet-AssociationEditor.js');
const libConfigSync = require('./ConfigSync.js');
const libProjectEditAccess = require('../access/Project-Edit-Access.js');

// The association-editor tabs — each a picker + current-list over a Project join, anchored on the Project side.
// Their host divs are re-created on every tab paint, so _mountTab re-mounts whichever one is on screen.
// NOTE: Equipment↔contractor is NOT here — it moved to the ProjectContractorMatrixEquipment M:N grid (equipment can
// have many contractors, matching ProjectEquipmentJoin's ternary key + Fieldbook), mounted like the line/bid matrices.
const ASSOC_TABS =
[
	// SyncOnChange: associating/removing an observation type changes what the field apps should offer for this
	// project, so a scoped Project config sync is fired (debounced) after each add/remove — see _ensureAssocEditorView.
	{ Host: '#PW-ObsHost', Hash: 'PW-ObsAssocEditor', Association: 'ProjectObservationManifest', SyncOnChange: true },
	{ Host: '#PW-LabHost', Hash: 'PW-LabAssocEditor', Association: 'LabProject' },
	{ Host: '#PW-ContactHost', Hash: 'PW-ContactAssocEditor', Association: 'ContactProject' },
	{ Host: '#PW-OrgHost', Hash: 'PW-OrgAssocEditor', Association: 'OrganizationProject' },
];

// Which per-tab template each leaf key paints (reused verbatim from the original view via parseTemplateByHash).
const TAB_TEMPLATE =
{
	overview: 'ProjectWorkspace-Overview', location: 'ProjectWorkspace-Location',
	lineitems: 'ProjectWorkspace-LineItems', biditems: 'ProjectWorkspace-BidItems', bidmap: 'ProjectWorkspace-BidItemMap',
	branding: 'ProjectWorkspace-Branding', labels: 'ProjectWorkspace-Labels', observations: 'ProjectWorkspace-Observations',
	equipment: 'ProjectWorkspace-Equipment', lab: 'ProjectWorkspace-Lab', contact: 'ProjectWorkspace-Contact', organization: 'ProjectWorkspace-Organization',
	linecontractors: 'ProjectWorkspace-LineContractors', bidcontractors: 'ProjectWorkspace-BidContractors',
	dates: 'ProjectWorkspace-Dates', staffing: 'ProjectWorkspace-Staffing', funding: 'ProjectWorkspace-Funding', changeorders: 'ProjectWorkspace-ChangeOrders',
	sampleplan: 'ProjectWorkspace-SamplePlan',
	sampleplandashboard: 'ProjectWorkspace-SamplePlanDashboard',
	'data-observations': 'ProjectWorkspace-DataObs', 'data-forms': 'ProjectWorkspace-DataForms',
	'data-files': 'ProjectWorkspace-Files', 'data-bulk-upload': 'ProjectWorkspace-BulkUpload', 'data-volume': 'ProjectWorkspace-DataVolume',
};

const PROJECT_LOGO_SLOTS =
[
	{ Key: 'logo', Label: 'Project logo', Base: 'Project/PrimaryLogo', Help: 'A PNG shown for this project across Headlight. Falls back to the customer logo when unset.' },
	{ Key: 'secondary', Label: 'Project reports logo', Base: 'Project/SecondaryLogo', Help: 'A PNG used on this project’s generated reports and documents.' },
];

// A Project is the richest config record in the platform: identity + schedule + financials + contract
// relationships + a geographic center (Latitude/Longitude) + a nested CustomProperties JSON blob +
// several connection sets (Observation Types, …). The stock recordset read renders a flat manifest
// form, which (a) coerces the parsed CustomProperties object to "[object Object]" and (b) can't host a
// map. This bespoke workspace owns the whole Project view: grouped read-only fields, formatted JSON, a
// Leaflet center-picker, and the association tabs — the same "bespoke UI for a complex config segment"
// pattern as the Observation Type builder. The pict-section-recordset list stays the browse surface.

// Read-only field groups for the Overview tab (declarative so the view iterates; values are formatted
// into display strings here — one-shot derived display data, not markup).
const FIELD_GROUPS =
[
	{ Name: 'Identity', Fields:
		[
			{ Key: 'Name', Label: 'Project Name', Wide: true },
			{ Key: 'ExternalProjectID', Label: 'External Project ID' },
			{ Key: 'Status', Label: 'Status' },
			{ Key: 'ImprovementTypeCode', Label: 'Improvement Type' },
		] },
	{ Name: 'Schedule', Fields:
		[
			{ Key: 'OriginalContractedDays', Label: 'Original Contracted Days', Format: 'number' },
			{ Key: 'ContractedDays', Label: 'Contracted Days', Format: 'number' },
			{ Key: 'CompletedDays', Label: 'Completed Days', Format: 'number' },
			{ Key: 'PaidDays', Label: 'Paid Days', Format: 'number' },
			{ Key: 'PercentComplete', Label: 'Percent Complete', Format: 'percent' },
			{ Key: 'PercentCompleteByDays', Label: 'Percent Complete (by days)', Format: 'percent' },
			{ Key: 'Timezone', Label: 'Timezone' },
		] },
	// Actions: 'financials' hangs the "Calculate initial contract" button off this group's header —
	// the only group with an action today.
	{ Name: 'Financials', Actions: 'financials', Fields:
		[
			{ Key: 'CostPerDay', Label: 'Cost / Day', Format: 'currency' },
			{ Key: 'InitialContractAmount', Label: 'Initial Contract', Format: 'currency' },
			{ Key: 'CurrentTotalContractAmount', Label: 'Current Total Contract', Format: 'currency' },
			{ Key: 'CurrentTotalContractPaid', Label: 'Total Paid', Format: 'currency' },
			{ Key: 'PurchaseOrderNumber', Label: 'Purchase Order #' },
		] },
	{ Name: 'Relationships', Fields:
		[
			{ Key: 'IDOrganization', Label: 'Prime Contractor', Card: 'Organization' },
			{ Key: 'IDContract', Label: 'Contract', Card: 'Contract' },
			{ Key: 'IDProjectType', Label: 'Project Type', Card: 'ProjectType' },
			{ Key: 'IDCustomer', Label: 'Customer', Card: 'Customer' },
		] },
	{ Name: 'Audit', Fields:
		[
			{ Key: 'CreateDate', Label: 'Created', Format: 'date' },
			{ Key: 'UpdateDate', Label: 'Last Updated', Format: 'date' },
			{ Key: 'GUIDProject', Label: 'GUID', Mono: true },
			{ Key: 'IDProject', Label: 'Project ID', Format: 'id' },
		] },
];

/**
 * ProjectWorkspace — the data layer behind the bespoke Project view. Loads a project, exposes a
 * grouped read-only render model, formats the CustomProperties JSON, holds the editable geographic
 * center, and persists the center back through Meadow. State lives in pict.AppData.ProjectWorkspace.
 */
class ProjectWorkspaceProvider extends libWorkspace.HeadlightWorkspaceBaseProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {any} */
		this.pict;

		// The working model (null until a load). Held outside AppData so the render state derives cleanly each paint.
		this._project = null;
		// Serializes association-editor renders (only one assoc tab is on screen at a time).
		this._projAssocQueue = null;
		this._dataSearchTimers = {};
		// The tab the workspace is showing.
		this._activeTab = 'overview';
		// Working geographic center (decimal degrees), separate from the loaded record so the map can edit
		// it before a save.
		this._center = { Latitude: null, Longitude: null };
		this._centerDirty = false;
		this._savingCenter = false;
		// Inline validation error for the editable lat/lng inputs (cleared on any successful center set).
		this._locError = '';
		// Address-search (Nominatim) working state: the top matches, an in-flight flag, and an error string.
		this._addressResults = [];
		this._addressSearching = false;
		this._addressError = '';
		this._logoController = new libLogoSlotController(this.pict, 'ProjectWorkspace', PROJECT_LOGO_SLOTS);
		this._labelController = new libLabelManagerController(this.pict, 'ProjectWorkspace');
		// Per-observation-type label BEHAVIORS (Free Entry / Max Count / Hidden + Auto-Filter) — stored in the
		// ObservationType Configuration blob, a different axis from the vocabulary above (see LabelBehaviorController).
		this._labelBehaviorController = new libLabelBehaviorController(this.pict, 'ProjectWorkspace');
		// The Projects-app "labels" feature: a project's actual labels (ObservationTagProjectJoin), added by
		// picking a type from the vocabulary above. Distinct from _labelController (the ProjectTagType types).
		this._obsLabelController = new libProjectObservationLabelController(this.pict, 'ProjectWorkspace');
		// Observation-type defaults fallback (read-only) when the project has no joins of its own.
		this._obsDefaults = [];
		this._obsShowingDefaults = false;
		// The project's Line Items / Bid Items (loaded lazily for the Items tabs), each { Loaded, Rows }.
		this._lineItems = { Loaded: false, Rows: [] };
		this._bidItems = { Loaded: false, Rows: [] };
		// Data ▸ Observations / Forms lists — server-paged (a project can carry thousands of each), with per-list
		// page + free-text search. Loaded lazily when their tab first opens.
		this._dataLists = this._freshDataLists();
		// In-flight flag for the Financials group's "Calculate initial contract" action.
		this._calculatingContractAmount = false;
	}

	/** Role 5+ (admin) or role 4 editing a project in their own customer can manage project logos. */
	get canManageProjectLogo()
	{
		const tmpUser = (this.pict && this.pict.AppData && this.pict.AppData.CurrentUser) || {};
		return libProjectEditAccess.isProjectAdministrator(tmpUser, this._project);
	}

	/**
	 * May the current user save the Project record (gates the header Edit action)? The server's rule: admin, role 4 in
	 * the project's customer, or the project's creator (see access/Project-Edit-Access.js).
	 * @return {boolean}
	 */
	get canEditProject()
	{
		const tmpUser = (this.pict && this.pict.AppData && this.pict.AppData.CurrentUser) || {};
		return libProjectEditAccess.canEditProject(tmpUser, this._project);
	}

	/** Same gate as project logos: admin, or a role-4 customer admin on a project in their own customer. */
	get canManageLabels()
	{
		return this.canManageProjectLogo;
	}

	/** Headlight super-user (role index 5, customer 1) — gates the manual "Run Config Sync" header action. */
	get isSuperUser()
	{
		const tmpUser = (this.pict && this.pict.AppData && this.pict.AppData.CurrentUser) || {};
		return (Number(tmpUser.UserRoleIndex) === 5) && (Number(tmpUser.CustomerID) === 1);
	}

	/**
	 * Manually trigger a SCOPED Configuration Sync for this project (header action; super-user only). Confirms,
	 * then fires immediately (no debounce) against the scoped endpoint, which rebuilds only this project.
	 * @return {Promise<void>}
	 */
	async syncConfiguration()
	{
		if (!this.idProject) { return; }
		const tmpModal = this.pict.views['Pict-Section-Modal'];
		const tmpName = (this._project && this._project.Name) || `Project ${this.idProject}`;
		const tmpConfirmed = (tmpModal && (typeof tmpModal.confirm === 'function'))
			? await tmpModal.confirm(`Run a configuration sync for ${tmpName}? This publishes the project's observation-type configuration out to the field apps. It runs in the background and may take a little while.`,
				{ title: 'Run configuration sync?', confirmLabel: 'Run sync', cancelLabel: 'Cancel' })
			: true;
		if (!tmpConfirmed) { return; }
		libConfigSync.requestScopedSync(this.pict, 'Project', Number(this.idProject), { immediate: true });
	}

	/** Recalculating the initial contract amount writes to the Project, so it needs the same gate as the other project edits. */
	get canEditProjectFinancials()
	{
		return this.canManageProjectLogo;
	}

	/** The bulk-delete tools are gated by the 'Project-BulkDelete' entitlement (access/Task-Catalog.js). */
	get canBulkDelete()
	{
		try
		{
			const tmpApp = this.pict.fable;   // the EffectivePermissionsService lives on fable, not the app instance
			if (tmpApp && tmpApp.EffectivePermissionsService && (typeof tmpApp.EffectivePermissionsService.can === 'function'))
			{
				return tmpApp.EffectivePermissionsService.can('Project', 'BulkDelete');
			}
		}
		catch (pError) { /* default closed */ }
		return false;
	}

	/**
	 * Open the project-scoped bulk-delete tool for one of the workspace's long lists. Line/bid items delete the
	 * records (soft); equipment/personnel/labels remove the project's link rows (the shared records stay). On
	 * close the affected tab is refreshed so it reflects the deletions.
	 * @param {'lineitems'|'biditems'|'equipment'|'contact'|'labels'} pKey
	 */
	openBulkDelete(pKey)
	{
		if (!this.canBulkDelete || !this._project) { return; }
		const tmpID = this._project.IDProject;
		// Closing the inline tool clears bulk mode, runs the per-list refresh, and repaints (which restores the
		// tab's normal read list and re-mounts any embedded editor).
		const fClose = (pRefresh) => () => { this._bulkDeleteKey = null; this._bulkSpec = null; if (typeof pRefresh === 'function') { pRefresh(); } this._render(); };
		const tmpHost = '#PW-BulkInlineHost';
		const tmpSpecs =
		{
			lineitems: {
				Title: 'Delete line items', Noun: 'line item', NounPlural: 'line items', Verb: 'Delete', Host: tmpHost,
				DeleteEntity: 'LineItem', ItemsSet: 'LineItems', IDField: 'IDLineItem',
				Filter: `FBV~IDProject~EQ~${tmpID}~FBV~Deleted~EQ~0`, Sort: 'ItemNumber',
				SearchFields: [ 'ItemNumber', 'Name' ],
				Columns: [ { Key: 'ItemNumber', Label: 'Item #' }, { Key: 'Name', Label: 'Description' } ],
				OnClose: fClose(() => { this._lineItems = { Loaded: false, Rows: [] }; if (this._activeTab === 'lineitems') { this._loadItemsAsync('lineitems'); } }),
			},
			biditems: {
				Title: 'Delete bid items', Noun: 'bid item', NounPlural: 'bid items', Verb: 'Delete', Host: tmpHost,
				DeleteEntity: 'BidItem', ItemsSet: 'BidItems', IDField: 'IDBidItem',
				Filter: `FBV~IDProject~EQ~${tmpID}~FBV~Deleted~EQ~0`, Sort: 'Name',
				SearchFields: [ 'Name' ],
				Columns: [ { Key: 'Name', Label: 'Name' }, { Key: 'Units', Label: 'Units' } ],
				OnClose: fClose(() => { this._bidItems = { Loaded: false, Rows: [] }; if (this._activeTab === 'biditems') { this._loadItemsAsync('biditems'); } }),
			},
			equipment: {
				Title: 'Unlink equipment from this project', Noun: 'equipment link', NounPlural: 'equipment links', Verb: 'Unlink', Host: tmpHost,
				DeleteEntity: 'ProjectEquipmentJoin', ItemsSet: 'ProjectEquipmentJoins', IDField: 'IDProjectEquipmentJoin',
				Filter: `FBV~IDProject~EQ~${tmpID}`,
				NameResolver: { Set: 'Equipments', IDField: 'IDEquipment', NameField: 'Name', FKField: 'IDEquipment', Label: 'Equipment' },
				SearchResolver: { Set: 'Equipments', IDField: 'IDEquipment', NameFields: [ 'Name', 'Code' ], JoinFKField: 'IDEquipment' },
				Columns: [], OnClose: fClose(null),
			},
			contact: {
				Title: 'Unlink personnel from this project', Noun: 'personnel link', NounPlural: 'personnel links', Verb: 'Unlink', Host: tmpHost,
				DeleteEntity: 'ProjectContactJoin', ItemsSet: 'ProjectContactJoins', IDField: 'IDProjectContactJoin',
				Filter: `FBV~IDProject~EQ~${tmpID}`,
				NameResolver: { Set: 'Contacts', IDField: 'IDContact', NameField: 'NameFull', FKField: 'IDContact', Label: 'Personnel' },
				SearchResolver: { Set: 'Contacts', IDField: 'IDContact', NameFields: [ 'NameFull', 'NameFirst', 'NameLast' ], JoinFKField: 'IDContact' },
				Columns: [], OnClose: fClose(null),
			},
			labels: {
				Title: 'Delete labels', Noun: 'label', NounPlural: 'labels', Verb: 'Delete', Host: tmpHost,
				DeleteEntity: 'ObservationTagProjectJoin', ItemsSet: 'ObservationTagProjectJoins', IDField: 'IDObservationTagProjectJoin',
				Filter: `FBV~IDProject~EQ~${tmpID}`, Sort: 'Tag',
				SearchFields: [ 'Tag', 'TagType' ],
				Columns: [ { Key: 'Tag', Label: 'Label' }, { Key: 'TagType', Label: 'Type' } ],
				OnClose: fClose(() => { this._obsLabelController.load(); }),
			},
		};
		const tmpSpec = tmpSpecs[pKey];
		if (!tmpSpec) { return; }
		// Enter bulk mode: the active tab renders the inline host (instead of its read list); the view mounts
		// the tool into it after the paint (like the association editors).
		this._bulkDeleteKey = pKey;
		this._bulkSpec = tmpSpec;
		this._render();
	}

	uploadSlot(pKey, pInput)
	{
		return this._logoController.upload(pKey, pInput);
	}

	removeSlot(pKey)
	{
		return this._logoController.remove(pKey);
	}

	// --- Project-label delegates (the shared Headlight-LabelManager template calls these by provider name) ---

	labelAdd() { return this._labelController.add(); }
	labelUpdateName(pID, pValue) { return this._labelController.updateName(pID, pValue); }
	labelRemove(pID) { return this._labelController.remove(pID); }
	labelSetNew(pField, pValue) { return this._labelController.setNew(pField, pValue); }
	labelCopyDefaults() { return this._labelController.copyDefaults(); }

	// --- Label-behavior delegates (the shared Headlight-LabelBehavior template calls these by provider name) ---

	labelBehaviorSelectType(pType) { return this._labelBehaviorController.selectType(pType); }
	labelBehaviorSetFreeEntry(pTagType, pDeny) { return this._labelBehaviorController.setFreeEntry(pTagType, pDeny); }
	labelBehaviorSetMaxCount(pTagType, pValue) { return this._labelBehaviorController.setMaxCount(pTagType, pValue); }
	labelBehaviorSetHidden(pTagType, pHidden) { return this._labelBehaviorController.setHidden(pTagType, pHidden); }
	labelBehaviorSetDistilling(pEnabled) { return this._labelBehaviorController.setDistilling(pEnabled); }

	// --- Project observation-label delegates (the Headlight-ObsLabelManager template calls these by name) ---

	obsLabelAdd() { return this._obsLabelController.add(); }
	obsLabelRemove(pID) { return this._obsLabelController.remove(pID); }
	obsLabelSetNewTag(pValue) { return this._obsLabelController.setNewTag(pValue); }
	obsLabelPickType(pValue) { return this._obsLabelController.pickTagType(pValue); }
	obsLabelSetSearch(pValue) { return this._obsLabelController.setSearch(pValue); }
	obsLabelApplySearch() { return this._obsLabelController.applySearch(); }
	obsLabelClearSearch() { return this._obsLabelController.clearSearch(); }
	obsLabelSetFilterType(pValue) { return this._obsLabelController.setFilterType(pValue); }
	obsLabelPage(pDelta) { return this._obsLabelController.page(pDelta); }

	/** Bulk-add: add every pasted label under the chosen type (both stashed on the controller, so an unrelated
	 *  full re-render can't discard an unsubmitted paste). */
	obsLabelBulkAdd() { return this._obsLabelController.bulkAdd(this._obsLabelController.bulkTagType, this._obsLabelController.bulkTag); }
	obsLabelSetBulkTag(pValue) { return this._obsLabelController.setBulkTag(pValue); }
	obsLabelPickBulkType(pValue) { return this._obsLabelController.pickBulkType(pValue); }

	get restClient()
	{
		return this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient;
	}

	get currentUser()
	{
		return (this.pict && this.pict.AppData && this.pict.AppData.CurrentUser) || {};
	}

	// --- REST helpers ---

	_getJSON(pURL)
	{
		return new Promise((resolve, reject) =>
		{
			this.restClient.getJSON(pURL, (pError, pBody) => pError ? reject(pError) : resolve(pBody));
		});
	}

	_putJSON(pURL, pPayload)
	{
		return new Promise((resolve, reject) =>
		{
			this.restClient.putJSON(pURL, pPayload, (pError, pBody) => pError ? reject(pError) : resolve(pBody));
		});
	}

	_postJSON(pURL, pPayload)
	{
		return new Promise((resolve, reject) =>
		{
			this.restClient.postJSON(pURL, pPayload, (pError, pBody) => pError ? reject(pError) : resolve(pBody));
		});
	}

	_isTruthy(pValue)
	{
		return (pValue === true) || (pValue === 1) || (pValue === '1') || (String(pValue).toLowerCase() === 'true');
	}

	_escapeHtml(pValue)
	{
		return String(pValue == null ? '' : pValue).replace(/[&<>"']/g, (pChar) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[pChar]));
	}

	/**
	 * Background-load the project's observation-type joins; if it has none of its own, fall back to the
	 * customer default set (IDProject=0 for this customer, then the global IDCustomer=0 set) shown read-only
	 * on the Observation Types tab — so the tab reflects what the project actually uses until customized.
	 * @param {Number} pIDProject
	 * @param {Number} pIDCustomer
	 */
	async _loadObservationDefaultsAsync(pIDProject, pIDCustomer)
	{
		this._obsDefaults = [];
		this._obsShowingDefaults = false;
		try
		{
			const tmpOwn = await this._getJSON(`ProjectObservationManifestJoins/FilteredTo/FBV~IDProject~EQ~${pIDProject}`);
			if (Array.isArray(tmpOwn) && tmpOwn.length > 0) { this._render(); return; }
			let tmpDefaults = await this._getJSON(`ProjectObservationManifestJoins/FilteredTo/FBV~IDProject~EQ~0~FBV~IDCustomer~EQ~${pIDCustomer}`);
			if (!Array.isArray(tmpDefaults) || tmpDefaults.length === 0)
			{
				tmpDefaults = await this._getJSON('ProjectObservationManifestJoins/FilteredTo/FBV~IDProject~EQ~0~FBV~IDCustomer~EQ~0');
			}
			this._obsDefaults = Array.isArray(tmpDefaults) ? tmpDefaults : [];
			this._obsShowingDefaults = this._obsDefaults.length > 0;
		}
		catch (pError) { this._obsDefaults = []; this._obsShowingDefaults = false; }
		this._render();
	}

	// --- Loading (base owns loadAsync/openByGUIDAsync; hydrateAsync runs after the Project record arrives) ---

	async hydrateAsync(pRecord)
	{
		this._project = pRecord;
		this._relatedDisplay = {};
		this._lineItems = { Loaded: false, Rows: [] };
		this._bidItems = { Loaded: false, Rows: [] };
		this._dataLists = this._freshDataLists();
		this._center = { Latitude: this._toNumberOrNull(pRecord.Latitude), Longitude: this._toNumberOrNull(pRecord.Longitude) };
		this._centerDirty = false;
		this._logoController.setContext(pRecord.IDProject, this.canManageProjectLogo, () => this._render());
		this._labelController.setContext({ IDProject: pRecord.IDProject, IDCustomer: pRecord.IDCustomer }, this.canManageLabels, () => this._render(), true);
		this._labelBehaviorController.setContext({ IDProject: pRecord.IDProject, IDCustomer: pRecord.IDCustomer }, this.canManageLabels, () => this._syncLabelBehavior());
		this._obsLabelController.setContext({ IDProject: pRecord.IDProject, IDCustomer: pRecord.IDCustomer }, this.canManageLabels, () => this._syncObsLabels());
		// Background loads (fire-and-forget; each repaints when ready) — NOT awaited so the first paint is immediate.
		this._logoController.probeAll().then(() => this._render());
		this._labelController.load();
		this._labelBehaviorController.load();
		this._obsLabelController.load();
		this._loadObservationDefaultsAsync(pRecord.IDProject, pRecord.IDCustomer);
		this._resolveRelatedDisplayAsync(pRecord);
	}

	// --- Project items (Line Items / Bid Items) ---

	/**
	 * Lazily fetch the project's Line Items or Bid Items (server-filtered by IDProject) for the Items
	 * tabs. Read-only "see all my project info in one place" — one page is plenty for the config view.
	 * @param {'lineitems'|'biditems'} pWhich
	 */
	async _loadItemsAsync(pWhich)
	{
		if (!this._project || !this._project.IDProject) { return; }
		const tmpState = (pWhich === 'biditems') ? this._bidItems : this._lineItems;
		if (tmpState.Loaded || tmpState.Loading) { return; }
		tmpState.Loading = true;
		const tmpEntity = (pWhich === 'biditems') ? 'BidItems' : 'LineItems';
		const tmpItemFilter = `FBV~IDProject~EQ~${this._project.IDProject}`;
		const tmpPageSize = 500;
		try
		{
			// Also read the true total so a project with more than one page of pay items shows "showing 500 of N"
			// instead of silently reporting the truncated page length as the total (a large multi-year DOT project
			// can carry several hundred change-order items). Count is best-effort — the list still renders without it.
			const [ tmpRows, tmpCount ] = await Promise.all(
				[
					this._getJSON(`${tmpEntity}/FilteredTo/${tmpItemFilter}/0/${tmpPageSize}`),
					this._getJSON(`${tmpEntity}/Count/FilteredTo/${tmpItemFilter}`).catch(() => null),
				]);
			tmpState.Rows = Array.isArray(tmpRows) ? tmpRows : [];
			tmpState.Total = (tmpCount && (tmpCount.Count != null)) ? Number(tmpCount.Count) : tmpState.Rows.length;
			tmpState.PageSize = tmpPageSize;
		}
		catch (pError)
		{
			tmpState.Rows = [];
			tmpState.Total = 0;
			this.pict.log.warn(`ProjectWorkspace: could not load ${tmpEntity} for project ${this._project.IDProject}: ${pError && (pError.message || pError)}`);
		}
		tmpState.Loading = false;
		tmpState.Loaded = true;
		this._render();
	}

	// --- Data lists (Observations / Forms) --------------------------------------------------------

	_freshDataLists()
	{
		return {
			'data-observations': { Loaded: false, Loading: false, Rows: [], Total: 0, Term: '', Page: 0 },
			'data-forms': { Loaded: false, Loading: false, Rows: [], Total: 0, Term: '', Page: 0 },
		};
	}

	/** Read a data list's state for the view. */
	dataList(pKind) { return (this._dataLists && this._dataLists[pKind]) || null; }
	get dataPageSize() { return 50; }

	/** Per-kind query config. Observations carry a readable `ObservationType`; Forms are the project's Documents
	 *  with a readable `DocumentType` — neither needs a secondary lookup. Both are name-searchable + date-sorted. */
	_dataListConfig(pKind)
	{
		const tmpID = this._project ? this._project.IDProject : 0;
		if (pKind === 'data-forms')
		{
			// Sort by the same field the list shows (DocumentDate) so the Date column reads monotonically.
			return { Entity: 'Documents', Filter: `FBV~IDProject~EQ~${tmpID}~FBV~Deleted~EQ~0`, SearchField: 'Name', Sort: 'DocumentDate' };
		}
		return { Entity: 'Observations', Filter: `FBV~IDProject~EQ~${tmpID}~FBV~Deleted~EQ~0`, SearchField: 'Name', Sort: 'CreateDate' };
	}

	/**
	 * Load one page of a data list (server-paged + optional name search). pOpts: { Term, Page, Quiet }. The Quiet
	 * path (search / paging) repaints only the results container so the search box keeps focus; the non-quiet path
	 * (first open) does a full workspace render so the tab's shell appears.
	 * @param {'data-observations'|'data-forms'} pKind
	 * @param {{Term?:String, Page?:Number, Quiet?:Boolean}} [pOpts]
	 */
	async loadDataListAsync(pKind, pOpts)
	{
		const tmpState = this.dataList(pKind);
		if (!tmpState || !this._project || !this._project.IDProject) { return; }
		const tmpOpts = pOpts || {};
		// First-open guard: don't reload an already-loaded list just because the tab repainted.
		if (!tmpOpts.Quiet && (tmpOpts.Term == null) && (tmpOpts.Page == null) && tmpState.Loaded) { return; }
		if (tmpOpts.Term != null) { tmpState.Term = tmpOpts.Term; }
		tmpState.Page = (tmpOpts.Page != null) ? tmpOpts.Page : ((tmpOpts.Term != null) ? 0 : tmpState.Page);
		tmpState.Loading = true;
		const tmpCfg = this._dataListConfig(pKind);
		const tmpClean = String(tmpState.Term || '').replace(/~/g, '').trim();
		let tmpFilter = tmpCfg.Filter;
		if (tmpClean) { tmpFilter += `~FBV~${tmpCfg.SearchField}~LK~${encodeURIComponent(`%${tmpClean}%`)}`; }
		const tmpBegin = tmpState.Page * this.dataPageSize;
		const fFetch = (pWithSort) =>
		{
			const tmpSort = pWithSort ? `~FSF~${tmpCfg.Sort}~DESC~0` : '';
			return this._getJSON(`${tmpCfg.Entity}/FilteredTo/${tmpFilter}${tmpSort}/${tmpBegin}/${this.dataPageSize}`);
		};
		try
		{
			let tmpRows;
			try { tmpRows = await fFetch(true); }
			catch (pSortErr) { tmpRows = await fFetch(false); }   // some fields don't accept the sort — fall back unsorted
			const tmpCount = await this._getJSON(`${tmpCfg.Entity}/Count/FilteredTo/${tmpFilter}`).catch(() => null);
			tmpState.Rows = Array.isArray(tmpRows) ? tmpRows : [];
			tmpState.Total = (tmpCount && (tmpCount.Count != null)) ? Number(tmpCount.Count) : tmpState.Rows.length;
		}
		catch (pError)
		{
			tmpState.Rows = []; tmpState.Total = 0;
			this.pict.log.warn(`ProjectWorkspace: could not load ${pKind} for project ${this._project.IDProject}: ${pError && (pError.message || pError)}`);
		}
		tmpState.Loading = false;
		tmpState.Loaded = true;
		if (tmpOpts.Quiet) { this.paintDataResults(pKind); }
		else { this._render(); }
	}

	/**
	 * Shape the Line Items / Bid Items list into a render model for its tab: a loading slot, an empty
	 * slot, and formatted rows. Line Items show item code / description / quantity / unit cost; Bid Items
	 * show name / quantity / unit cost. Currency + number values are pre-formatted display strings.
	 * @param {'lineitems'|'biditems'} pWhich
	 */
	_buildItemsModel(pWhich)
	{
		const tmpIsLine = (pWhich !== 'biditems');
		const tmpState = tmpIsLine ? this._lineItems : this._bidItems;
		const tmpModel =
		{
			IsLineItems: tmpIsLine,
			Count: (tmpState.Rows || []).length,
			// Honest count: show "N of Total" when the page was truncated, else just the total.
			CountLabel: (function ()
			{
				const tmpShown = (tmpState.Rows || []).length;
				const tmpTotal = (tmpState.Total != null) ? tmpState.Total : tmpShown;
				return (tmpTotal > tmpShown) ? `${tmpShown} of ${tmpTotal}` : String(tmpTotal);
			})(),
			LoadingSlot: (!tmpState.Loaded) ? [ {} ] : [],
			EmptySlot: (tmpState.Loaded && ((tmpState.Rows || []).length === 0)) ? [ {} ] : [],
			// One-or-zero-element slot so the table template (which iterates rows) renders only when there
			// are rows — {~NE:~} would not recursively parse the inner {~TS:~} row loop.
			TableSlot: [],
			LineRows: [], BidRows: [],
			// "Delete records" button (gated by the Project-BulkDelete entitlement) — shown once the list has rows.
			BulkDeleteSlot: (this.canBulkDelete && tmpState.Loaded && ((tmpState.Rows || []).length > 0))
				? [ { Key: tmpIsLine ? 'lineitems' : 'biditems', Label: tmpIsLine ? 'Delete line items' : 'Delete bid items' } ]
				: [],
		};
		tmpModel.TableSlot = (tmpState.Loaded && ((tmpState.Rows || []).length > 0)) ? [ tmpModel ] : [];
		if (tmpIsLine)
		{
			tmpModel.LineRows = (tmpState.Rows || []).map((pRow) => (
				{
					GUID: this._escapeHtml(pRow.GUIDLineItem || ''),
					Code: this._escapeHtml(pRow.ItemCode || pRow.ItemNumber || '—'),
					Name: this._escapeHtml(pRow.Name || pRow.Description || '—'),
					Units: this._escapeHtml(pRow.Units || '—'),
					Quantity: this._formatValue({ Format: 'number' }, pRow.ContractQuantity),
					UnitCost: this._formatValue({ Format: 'currency' }, pRow.CostPerUnit),
				}));
		}
		else
		{
			tmpModel.BidRows = (tmpState.Rows || []).map((pRow) => (
				{
					GUID: this._escapeHtml(pRow.GUIDBidItem || ''),
					Name: this._escapeHtml(pRow.Name || pRow.Description || '—'),
					Units: this._escapeHtml(pRow.Units || '—'),
					Quantity: this._formatValue({ Format: 'number' }, pRow.Quantity),
					UnitCost: this._formatValue({ Format: 'currency' }, pRow.CostPerUnit),
				}));
		}
		return tmpModel;
	}

	// --- Tabs (base owns setTab/navigateTab/openTabAsync; each leaf's OnEnter calls _mountTab below) ---

	/**
	 * Rebuild one join type's tag index for this project — the old Projects app's per-join "Rebuild Tags"
	 * action. A side-effecting, authenticated GET with no body; the backend re-derives the project's
	 * searchable tags for that join from its current links. The response is inconsistent by design — either
	 * a bare JSON array of the rebuilt tag names, or an empty / 204 body — so both are accepted, and any
	 * failure is reported but treated as non-fatal (the underlying links already exist either way).
	 * @param {('Equipment'|'Contact'|'LineItem'|'BidItem')} pTagType - the RebuildTags path segment.
	 */
	async rebuildTags(pTagType)
	{
		const tmpIDProject = this.idProject;
		if (!tmpIDProject) { return; }
		const tmpState = this.workspaceState;
		tmpState.Message = `Rebuilding ${pTagType} tags…`;
		tmpState.MessageClass = 'is-info';
		this._render();
		try
		{
			const tmpResult = await this._getJSON(`RebuildTags/${encodeURIComponent(pTagType)}/${tmpIDProject}`);
			if (tmpResult && (tmpResult.ErrorCode || tmpResult.Error))
			{
				throw new Error(String(tmpResult.Error || tmpResult.ErrorCode));
			}
			const tmpCount = Array.isArray(tmpResult) ? tmpResult.length : null;
			tmpState.Message = (tmpCount != null)
				? `Rebuilt ${tmpCount.toLocaleString()} ${pTagType} tag${tmpCount === 1 ? '' : 's'}.`
				: `${pTagType} tags rebuilt.`;
			tmpState.MessageClass = 'is-success';
			this._render();
		}
		catch (pError)
		{
			tmpState.Message = `Could not rebuild ${pTagType} tags: ${pError.message || pError}`;
			tmpState.MessageClass = 'is-danger';
			this._render();
		}
	}

	// --- Geographic center ---

	get idProject()
	{
		return this._project ? this._project.IDProject : null;
	}

	/** The working center, falling back to a sensible default view when unset (0,0 is the null island). */
	get center()
	{
		return this._center;
	}

	get hasCenter()
	{
		return (this._center.Latitude != null) && (this._center.Longitude != null)
			&& !((Number(this._center.Latitude) === 0) && (Number(this._center.Longitude) === 0));
	}

	/**
	 * Set the working center from the map (marker drag / click). Updates state + the readout without a
	 * save; the user commits with Save.
	 * @param {Number} pLatitude
	 * @param {Number} pLongitude
	 */
	setCenter(pLatitude, pLongitude, pSyncMap)
	{
		this._center = { Latitude: Number(pLatitude), Longitude: Number(pLongitude) };
		this._centerDirty = true;
		this._locError = '';
		// Light update only: re-paint the readout + Save button + the lat/lng inputs, NOT the whole
		// workspace — a full render would recreate #PW-MapHost and tear down the live Leaflet map on
		// every marker drag/click.
		this.deriveRenderState();
		this.syncCenterControls();
		this.syncCenterInputs();
		// pSyncMap = a non-map-driven change (typed coordinates or an address result): move the live marker
		// and recenter so the map follows. Map gestures (drag/click) omit it — the marker already moved.
		if (pSyncMap)
		{
			const tmpMap = this.pict.views.ProjectCenterMap;
			if (tmpMap && (typeof tmpMap.moveMarkerTo === 'function')) { tmpMap.moveMarkerTo(this._center.Latitude, this._center.Longitude); }
		}
	}

	/**
	 * Set the working center from the editable latitude/longitude inputs. Validates ranges; on bad input
	 * it surfaces an inline error and leaves the center untouched.
	 * @param {string|number} pLatitude @param {string|number} pLongitude
	 */
	setCenterFromText(pLatitude, pLongitude)
	{
		const tmpLat = Number(pLatitude);
		const tmpLng = Number(pLongitude);
		if (!Number.isFinite(tmpLat) || !Number.isFinite(tmpLng) || (tmpLat < -90) || (tmpLat > 90) || (tmpLng < -180) || (tmpLng > 180))
		{
			this._locError = 'Enter a valid latitude (−90 to 90) and longitude (−180 to 180).';
			this.deriveRenderState();
			this.syncCenterControls();
			return;
		}
		this.setCenter(tmpLat, tmpLng, true);
	}

	/**
	 * Geocode the address-search box's text to coordinates via the public Nominatim (OpenStreetMap)
	 * service — the same provider as the map tiles, so no API key / new dependency. One request per
	 * explicit search (no as-you-type), keeping well under OSM's fair-use policy. Results populate a
	 * dropdown; picking one (chooseAddressResult) drops the pin.
	 */
	async searchAddressAsync()
	{
		const tmpInput = (typeof document !== 'undefined') ? document.getElementById('PW-AddressInput') : null;
		const tmpQuery = tmpInput ? String(tmpInput.value || '').trim() : '';
		if (!tmpQuery) { return; }
		this._addressSearching = true;
		this._addressError = '';
		this._addressResults = [];
		this._syncAddress();
		try
		{
			const tmpURL = `https://nominatim.openstreetmap.org/search?format=jsonv2&limit=5&q=${ encodeURIComponent(tmpQuery) }`;
			const tmpResponse = await fetch(tmpURL, { headers: { 'Accept': 'application/json' } });
			if (!tmpResponse.ok) { throw new Error(`the geocoder returned ${ tmpResponse.status }`); }
			const tmpRows = await tmpResponse.json();
			this._addressSearching = false;
			this._addressResults = (Array.isArray(tmpRows) ? tmpRows : [])
				.map((pRow) => ({ DisplayName: this._escapeHtml(pRow.display_name || 'result'), Latitude: Number(pRow.lat), Longitude: Number(pRow.lon) }))
				.filter((pRow) => Number.isFinite(pRow.Latitude) && Number.isFinite(pRow.Longitude))
				.slice(0, 5);
			this._addressError = (this._addressResults.length === 0) ? 'No matches found for that address.' : '';
			this._syncAddress();
		}
		catch (pError)
		{
			this._addressSearching = false;
			this._addressResults = [];
			this._addressError = `Address search failed: ${ pError.message || pError }`;
			this._syncAddress();
		}
	}

	/**
	 * Pick an address-search result: set the working center to its coordinates (drops the pin + recenters)
	 * and clear the results dropdown.
	 * @param {Number} pIndex
	 */
	chooseAddressResult(pIndex)
	{
		const tmpRow = (this._addressResults || [])[Number(pIndex)];
		if (!tmpRow) { return; }
		this._addressResults = [];
		this._addressError = '';
		this.setCenter(tmpRow.Latitude, tmpRow.Longitude, true);
		this._syncAddress();
	}

	/** Re-derive + repaint just the address-search results region (#PW-AddressResults). */
	_syncAddress()
	{
		this.deriveRenderState();
		this.syncAddressResults();
	}

	/**
	 * Re-derive + repaint JUST the observation-label (values) list region (#PW-ObsLabelHost) instead of the
	 * whole workspace. The values controller's add/remove/search/page all route here, so a label change no
	 * longer tears down the map / association editor / custom-props embeds on the other tabs, and the search
	 * filter (held on the controller instance) is re-emitted intact. Falls back to a full render if the host
	 * isn't mounted yet (e.g. the first background load, before the Labels tab has ever been shown).
	 */
	_syncObsLabels()
	{
		this.deriveRenderState();
		if (this.syncObsLabels()) { return; }
		this._render();
	}

	/**
	 * Persist the working center to the project (PUT Latitude/Longitude only — Meadow does a partial
	 * update). Surfaces a clean message if the role can't update this project (Update is role/ownership
	 * gated: User/Manager/Director = Mine, Executive = MyCustomer, Administrator = Allow).
	 * @return {Promise<void>}
	 */
	async saveCenterAsync()
	{
		if (!this._project || !this._centerDirty) { return; }
		const tmpState = this.workspaceState;
		this._savingCenter = true;
		tmpState.Message = '';
		this._render();
		try
		{
			const tmpSaved = await this._putJSON('Project', { IDProject: this._project.IDProject, Latitude: this._center.Latitude, Longitude: this._center.Longitude });
			if (tmpSaved && tmpSaved.ErrorCode)
			{
				throw new Error('the server rejected the update (you may not have permission to edit this project’s center).');
			}
			this._project.Latitude = this._center.Latitude;
			this._project.Longitude = this._center.Longitude;
			this._centerDirty = false;
			this._savingCenter = false;
			tmpState.Message = 'Geographic center saved.';
			tmpState.MessageClass = 'is-success';
			this._render();
		}
		catch (pError)
		{
			this._savingCenter = false;
			tmpState.Message = `Could not save the center: ${pError.message || pError}`;
			tmpState.MessageClass = 'is-danger';
			this._render();
		}
	}

	/**
	 * Ask the finance service to recalculate this project's initial contract amount from its contracted
	 * work items and stamp it onto the project. The service picks the source (bid items when they carry
	 * a value, line items otherwise) and reports anything the user should know about — a bid-vs-line
	 * disagreement, unpriced items — which is surfaced here rather than swallowed.
	 * @return {Promise<void>}
	 */
	async calculateInitialContractAmountAsync()
	{
		if (!this._project || this._calculatingContractAmount) { return; }
		const tmpState = this.workspaceState;
		this._calculatingContractAmount = true;
		tmpState.Message = '';
		this._render();
		try
		{
			const tmpResult = await this._postJSON(`Finance/ProjectSetup/InitialContractAmount/For/Project/${this._project.IDProject}`, {});
			if (!tmpResult || tmpResult.ErrorCode || tmpResult.code)
			{
				throw new Error(tmpResult && (tmpResult.message || tmpResult.Error) ?
					(tmpResult.message || tmpResult.Error) :
					'the server rejected the request (you may not have permission to edit this project).');
			}

			this._calculatingContractAmount = false;

			if (!tmpResult.Stamped)
			{
				tmpState.Message = tmpResult.Warnings && tmpResult.Warnings.length ? tmpResult.Warnings.join(' ') : 'There was nothing to calculate an initial contract amount from.';
				tmpState.MessageClass = 'is-warning';
				this._render();
				return;
			}

			this._project.InitialContractAmount = tmpResult.InitialContractAmount;

			const tmpFormatted = this._formatValue({ Format: 'currency' }, tmpResult.InitialContractAmount);
			const tmpPrevious = this._formatValue({ Format: 'currency' }, tmpResult.PreviousInitialContractAmount);
			const tmpSourceLabel = (tmpResult.Source === 'BidItem') ? 'bid items' : 'line items';
			let tmpMessage = `Initial contract set to ${tmpFormatted} from ${tmpResult.Source === 'BidItem' ? tmpResult.BidItemCount : tmpResult.LineItemCount} ${tmpSourceLabel} (was ${tmpPrevious}).`;
			if (tmpResult.Warnings && tmpResult.Warnings.length)
			{
				tmpMessage += ` ${tmpResult.Warnings.join(' ')}`;
			}
			tmpState.Message = tmpMessage;
			// a bid-vs-line disagreement is a real data problem the user should look at, not a success note
			tmpState.MessageClass = tmpResult.Discrepancy ? 'is-warning' : 'is-success';
			this._render();
		}
		catch (pError)
		{
			this._calculatingContractAmount = false;
			tmpState.Message = `Could not calculate the initial contract amount: ${pError.message || pError}`;
			tmpState.MessageClass = 'is-danger';
			this._render();
		}
	}

	// --- Value formatting (one-shot display strings) ---

	_toNumberOrNull(pValue)
	{
		if (pValue == null || pValue === '') { return null; }
		const tmpNumber = Number(pValue);
		return Number.isFinite(tmpNumber) ? tmpNumber : null;
	}

	_formatValue(pField, pValue)
	{
		if (pValue == null || pValue === '') { return '—'; }
		switch (pField.Format)
		{
			case 'currency':
			{
				const tmpNumber = Number(pValue);
				if (!Number.isFinite(tmpNumber)) { return String(pValue); }
				return tmpNumber.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
			}
			case 'percent':
			{
				const tmpNumber = Number(pValue);
				if (!Number.isFinite(tmpNumber)) { return String(pValue); }
				return `${Math.round(tmpNumber * 10) / 10}%`;
			}
			case 'number':
			{
				const tmpNumber = Number(pValue);
				if (!Number.isFinite(tmpNumber)) { return String(pValue); }
				return tmpNumber.toLocaleString('en-US');
			}
			case 'id':
			{
				// Identifiers are not quantities — render the integer WITHOUT thousands separators (no "1,234").
				const tmpIDNumber = Number(pValue);
				return Number.isFinite(tmpIDNumber) ? String(Math.trunc(tmpIDNumber)) : String(pValue);
			}
			case 'date':
			{
				const tmpString = String(pValue);
				if (tmpString.indexOf('0000-00-00') === 0) { return '—'; }
				const tmpDate = new Date(tmpString);
				return Number.isNaN(tmpDate.getTime()) ? tmpString : tmpDate.toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
			}
			default:
				return String(pValue);
		}
	}

	/**
	 * Resolve display names for the project's related entities (Organization, Contract, ProjectType,
	 * Customer) so the Overview "Relationships" fields render as named preview-card triggers rather than
	 * raw ids. Best-effort; re-renders when names arrive, and unresolved targets fall back to the id.
	 * @param {Record<string, any>} pProject
	 */
	async _resolveRelatedDisplayAsync(pProject)
	{
		const tmpTargets = [];
		FIELD_GROUPS.forEach((pGroup) => pGroup.Fields.forEach((pField) =>
		{
			const tmpID = pProject[pField.Key];
			if (pField.Card && (tmpID != null) && (Number(tmpID) > 0))
			{
				tmpTargets.push({ Key: pField.Key, RecordSet: pField.Card, ID: tmpID });
			}
		}));
		const tmpDisplay = {};
		await Promise.all(tmpTargets.map(async (pTarget) =>
		{
			try
			{
				const tmpRecord = await this._getJSON(`${pTarget.RecordSet}/${pTarget.ID}`);
				if (tmpRecord && !tmpRecord.Error)
				{
					tmpDisplay[pTarget.Key] = tmpRecord.Name || tmpRecord.DisplayName || tmpRecord.Title || String(pTarget.ID);
				}
			}
			catch (pError) { /* leave unresolved → the field falls back to the id */ }
		}));
		this._relatedDisplay = tmpDisplay;
		this._render();
	}

	/** Parse the CustomProperties value (object OR JSON string) into a plain object for the embedded
	 *  read-only ObjectEditor tree; returns null when empty or unparseable. */
	_parseCustomProperties(pValue)
	{
		if (pValue == null || pValue === '') { return null; }
		let tmpObject = pValue;
		if (typeof pValue === 'string')
		{
			try { tmpObject = JSON.parse(pValue); }
			catch (pError) { return null; }
		}
		return (tmpObject && typeof tmpObject === 'object') ? tmpObject : null;
	}

	/**
	 * The Financials group's action row. The button stays visible but inert when the user can't edit the
	 * project or a calculation is already running, with a title explaining which it is.
	 * @return {{ DisabledAttr: string, Title: string, BusySlot: object[] }}
	 */
	_buildFinancialsActions()
	{
		const tmpAllowed = this.canEditProjectFinancials;
		const tmpBusy = this._calculatingContractAmount;
		let tmpTitle = 'Recalculate this project’s initial contract amount from its bid items, or its line items when the bid items carry no cost.';
		if (!tmpAllowed) { tmpTitle = 'You do not have permission to edit this project.'; }
		else if (tmpBusy) { tmpTitle = 'Calculating…'; }

		return {
			DisabledAttr: (!tmpAllowed || tmpBusy) ? 'disabled' : '',
			Title: tmpTitle,
			BusySlot: tmpBusy ? [ {} ] : [],
		};
	}

	// --- Render-state ---

	/** The base uses `state` for its shell (Loading/Message/Tab); Project's slot-rich model IS that same object. */
	get state() { return this.workspaceState; }
	get workspaceState()
	{
		if (!this.pict.AppData.ProjectWorkspace)
		{
			this.pict.AppData.ProjectWorkspace =
			{
				Loading: false, Message: '', MessageClass: '', Tab: '',
				LoadingSlot: [], EmptySlot: [ {} ], MessageSlot: [], WorkspaceSlot: [],
				Eyebrow: 'Project', Title: 'Project',
				Tabs: [], OverviewSlot: [], LocationSlot: [], LabelsSlot: [], ObservationsSlot: [],
				Groups: [], DescriptionSlot: [], CustomJSONSlot: [], CustomPropsData: {}, LabelStateSlot: [], LabelBehaviorStateSlot: [], ObsDefaultsSlot: [], ObsDefaults: [],
				CenterLatText: '', CenterLngText: '', CenterSummary: '', CenterSaveSlot: [],
				CenterLatInput: '', CenterLngInput: '', LocErrorSlot: [], AddressStatusSlot: [], AddressResultSlot: [],
			};
		}
		return this.pict.AppData.ProjectWorkspace;
	}

	deriveRenderState()
	{
		const tmpState = this.workspaceState;
		tmpState.MessageSlot = tmpState.Message ? [ { Message: tmpState.Message, MessageClass: tmpState.MessageClass || 'is-info' } ] : [];

		if (tmpState.Loading)
		{
			tmpState.LoadingSlot = [ {} ];
			tmpState.WorkspaceSlot = [];
			tmpState.EmptySlot = [];
			return;
		}
		if (!this._project)
		{
			tmpState.LoadingSlot = [];
			tmpState.WorkspaceSlot = [];
			tmpState.EmptySlot = [ {} ];
			return;
		}

		const tmpProject = this._project;
		tmpState.Eyebrow = 'Project';
		tmpState.Title = tmpProject.Name || `Project ${tmpProject.IDProject}`;

		// The grouped tab structure now lives in the workspace config (Project.config.js); the base renders the
		// tab bar. deriveRenderState continues to populate the per-tab *Slot render models the tab templates read.
		tmpState.OverviewSlot = (this._activeTab === 'overview') ? [ {} ] : [];
		tmpState.DatesSlot = (this._activeTab === 'dates') ? [ {} ] : [];
		tmpState.StaffingSlot = (this._activeTab === 'staffing') ? [ {} ] : [];
		tmpState.LocationSlot = (this._activeTab === 'location') ? [ {} ] : [];
		// A long-list tab in bulk-delete mode renders the inline selectable tool INSTEAD of its read list (the
		// tool mounts into #PW-BulkInlineHost — see BulkInlineSlot below); the read list is hidden meanwhile.
		const tmpBulkKey = this._bulkDeleteKey;
		tmpState.LineItemsSlot = ((this._activeTab === 'lineitems') && (tmpBulkKey !== 'lineitems')) ? [ this._buildItemsModel('lineitems') ] : [];
		tmpState.BidItemsSlot = ((this._activeTab === 'biditems') && (tmpBulkKey !== 'biditems')) ? [ this._buildItemsModel('biditems') ] : [];
		tmpState.BrandingSlot = (this._activeTab === 'branding') ? [ {} ] : [];
		tmpState.LabelsSlot = ((this._activeTab === 'labels') && (tmpBulkKey !== 'labels')) ? [ {} ] : [];
		tmpState.ObservationsSlot = (this._activeTab === 'observations') ? [ {} ] : [];
		// Materials ▸ Sample Plan: a host the SamplePlan view embeds into (mounted by the view's _mountActiveTabEmbeds).
		tmpState.SamplePlanSlot = (this._activeTab === 'sampleplan') ? [ {} ] : [];
		// Contract ▸ Funding: a host the ProjectFunding view embeds into.
		tmpState.FundingSlot = (this._activeTab === 'funding') ? [ {} ] : [];
		// Contract ▸ Change Orders: a host the ChangeOrders view embeds into.
		tmpState.ChangeOrdersSlot = (this._activeTab === 'changeorders') ? [ {} ] : [];
		// Data ▸ Observations / Forms: hosts the view paints the server-paged lists into.
		tmpState.DataObsSlot = (this._activeTab === 'data-observations') ? [ {} ] : [];
		tmpState.DataFormsSlot = (this._activeTab === 'data-forms') ? [ {} ] : [];
		// Data ▸ Important Files: a host the ImportantFiles navigator embeds into.
		tmpState.FilesSlot = (this._activeTab === 'data-files') ? [ {} ] : [];
		// Data ▸ Bulk Upload: a host the BulkUpload tool embeds into.
		tmpState.BulkUploadSlot = (this._activeTab === 'data-bulk-upload') ? [ {} ] : [];
		// Data ▸ Data Volume: a host the ProjectActivity dashboard view embeds into.
		tmpState.DataVolumeSlot = (this._activeTab === 'data-volume') ? [ {} ] : [];
		// Association-editor join tabs (Equipment / Labs / Personnel). One-or-zero-element slot per tab; the
		// editor itself is mounted into the host div by the view's _mountActiveTabEmbeds after each paint.
		tmpState.EquipmentSlot = ((this._activeTab === 'equipment') && (tmpBulkKey !== 'equipment')) ? [ {} ] : [];
		tmpState.LabSlot = (this._activeTab === 'lab') ? [ {} ] : [];
		tmpState.ContactSlot = ((this._activeTab === 'contact') && (tmpBulkKey !== 'contact')) ? [ {} ] : [];
		tmpState.OrganizationSlot = (this._activeTab === 'organization') ? [ {} ] : [];
		// Inline bulk-delete host — rendered (in the active tab's panel) whenever a bulk-delete session is open.
		tmpState.BulkInlineSlot = tmpBulkKey ? [ {} ] : [];
		// Bulk-delete buttons (gated by the Project-BulkDelete entitlement) for the join-based long lists.
		const tmpCanBulk = this.canBulkDelete;
		tmpState.BulkDelEquipSlot = tmpCanBulk ? [ { Key: 'equipment', Label: 'Bulk unlink' } ] : [];
		tmpState.BulkDelContactSlot = tmpCanBulk ? [ { Key: 'contact', Label: 'Bulk unlink' } ] : [];
		tmpState.BulkDelLabelSlot = tmpCanBulk ? [ { Key: 'labels', Label: 'Bulk delete labels' } ] : [];
		// Contractor-matrix tabs (Line Item / Bid Item contractors). The grid provider is mounted into the host
		// div by the view's _mountActiveTabEmbeds after each paint (like the association editors).
		tmpState.LineContractorSlot = (this._activeTab === 'linecontractors') ? [ {} ] : [];
		tmpState.BidContractorSlot = (this._activeTab === 'bidcontractors') ? [ {} ] : [];
		tmpState.LogoSlots = this._logoController.buildRecords();
		tmpState.LabelStateSlot = [ this._labelController.buildState() ];
		tmpState.LabelBehaviorStateSlot = [ this._labelBehaviorController.buildState() ];
		tmpState.ObsLabelStateSlot = [ this._obsLabelController.buildState() ];
		tmpState.ObsDefaultsSlot = this._obsShowingDefaults ? [ {} ] : [];
		tmpState.ObsDefaults = (this._obsDefaults || []).map((pJoin) =>
			(
				{
					Name: this._escapeHtml(pJoin.ObservationManifestName || '(unnamed)'),
					Modes: [ this._isTruthy(pJoin.Journal) ? 'Journal' : null, this._isTruthy(pJoin.Spreadsheet) ? 'Spreadsheet' : null ].filter(Boolean).join(' · ') || '—',
				}));

		// Overview field groups.
		tmpState.Groups = FIELD_GROUPS.map((pGroup) =>
			(
				{
					Name: pGroup.Name,
					ActionsSlot: (pGroup.Actions === 'financials') ? [ this._buildFinancialsActions() ] : [],
					Fields: pGroup.Fields.map((pField) =>
						{
							const tmpRaw = tmpProject[pField.Key];
							// A relationship field with a registered preview card renders as a card trigger
							// (resolved entity name + popover) in place of the raw id — the name is resolved
							// lazily by _resolveRelatedDisplayAsync, falling back to the id until it arrives.
							const tmpCardOK = !!pField.Card && (tmpRaw != null) && (Number(tmpRaw) > 0);
							const tmpName = (this._relatedDisplay && this._relatedDisplay[pField.Key]) || '';
							return {
								Label: pField.Label,
								Value: tmpCardOK ? (tmpName || String(tmpRaw)) : (pField.Card ? '—' : this._formatValue(pField, tmpRaw)),
								CardRecordSet: tmpCardOK ? pField.Card : '',
								CardID: tmpCardOK ? String(tmpRaw) : '',
								WideClass: pField.Wide ? 'pw-field-wide' : '',
								MonoClass: pField.Mono ? 'pw-field-mono' : '',
							};
						}),
				}));
		const tmpDescription = (tmpProject.Description != null && String(tmpProject.Description).trim() !== '') ? String(tmpProject.Description) : '';
		tmpState.DescriptionSlot = tmpDescription ? [ { Description: tmpDescription } ] : [];
		// CustomProperties → a read-only ObjectEditor tree (the view mounts it into #PW-CustomPropsHost,
		// reading the stashed object); the slot just gates whether the group renders at all.
		const tmpCustomProps = this._parseCustomProperties(tmpProject.CustomProperties);
		tmpState.CustomPropsData = tmpCustomProps || {};
		tmpState.CustomJSONSlot = (tmpCustomProps && Object.keys(tmpCustomProps).length > 0) ? [ {} ] : [];

		// Location / center.
		tmpState.CenterLatText = (this._center.Latitude != null) ? Number(this._center.Latitude).toFixed(6) : '—';
		tmpState.CenterLngText = (this._center.Longitude != null) ? Number(this._center.Longitude).toFixed(6) : '—';
		tmpState.CenterSummary = this.hasCenter ? `${tmpState.CenterLatText}, ${tmpState.CenterLngText}` : 'Not set';
		tmpState.CenterSaveSlot = this._centerDirty ? [ { Saving: this._savingCenter } ] : [];
		tmpState.SaveDisabledAttr = this._savingCenter ? 'disabled' : '';
		// Editable lat/lng input values (raw number or empty — distinct from the '—' display readout above).
		tmpState.CenterLatInput = (this._center.Latitude != null) ? String(Number(this._center.Latitude)) : '';
		tmpState.CenterLngInput = (this._center.Longitude != null) ? String(Number(this._center.Longitude)) : '';
		tmpState.LocErrorSlot = this._locError ? [ { Text: this._locError } ] : [];

		// Address search: a single status line (searching / error / no-match) + the clickable result rows.
		const tmpAddressStatus = this._addressSearching ? 'Searching…' : (this._addressError || '');
		tmpState.AddressStatusSlot = tmpAddressStatus ? [ { Text: tmpAddressStatus, ErrorClass: this._addressSearching ? '' : 'pw-addr-status-error' } ] : [];
		tmpState.AddressResultSlot = (this._addressResults || []).map((pRow, pIndex) => ({ DisplayName: pRow.DisplayName, Index: pIndex }));

		tmpState.LoadingSlot = [];
		tmpState.EmptySlot = [];
		tmpState.WorkspaceSlot = [ tmpState ];
	}

	/** The ported methods below call _render for a full repaint; on the base that is renderWorkspace (which repaints
	 *  header + tab bar + the active tab body, then re-runs the active tab's OnEnter → _mountTab to re-mount widgets). */
	_render() { this.renderWorkspace(); }

	backToList()
	{
		if (typeof window !== 'undefined' && window.location)
		{
			window.location.hash = '#/PSRS/Project/List';
		}
	}

	/** Open this project's Sample Plan editor (material sampling/testing across its line items). */
	goToSamplePlan()
	{
		if (this._project && this._project.IDProject && (typeof window !== 'undefined') && window.location)
		{
			window.location.hash = `#/SamplePlan/${this._project.IDProject}`;
		}
	}

	/**
	 * Open the loaded project in the bespoke Project edit screen (the router bridges `/PSRS/Project/Edit/:GUIDRecord`
	 * to ProjectEditWorkspace). Only reachable from the header Edit action, which canEditProject gates.
	 */
	editProject()
	{
		if (!this._project || !this._project.GUIDProject)
		{
			return;
		}
		if (!this.canEditProject)
		{
			return;
		}
		if (typeof window !== 'undefined' && window.location)
		{
			window.location.hash = `#/PSRS/Project/Edit/${ this._project.GUIDProject }`;
		}
	}

	// --- Base `render` tab dispatch: paint the active tab's original template, then (re)mount its widgets --------

	/** Every tab is a `render` leaf pointing here: refresh the render model, then paint the tab's template. */
	_renderTabBody(pProv, pBodySel)
	{
		this.deriveRenderState();
		// The base calls the leaf OnEnter (→ _mountTab) on tab ENTRY, but a plain repaint (_renderActiveBody — e.g.
		// late boot data landing on a cold deep-link) re-paints this body WITHOUT re-mounting the tab's embeds,
		// wiping the map / association editor / bid-item-mapping host. Re-mount on the next tick after this paint so
		// an on-screen embed always survives a repaint (debounced; _mountTab only touches on-screen hosts + is idempotent).
		if (!this._remountScheduled && (typeof setTimeout === 'function'))
		{
			this._remountScheduled = true;
			setTimeout(() => { this._remountScheduled = false; try { this._mountTab(); } catch (pErr) { /* mount is best-effort */ } }, 0);
		}
		const tmpTab = this._activeTab;
		// Bulk-delete mode: the active long-list tab shows the inline selectable tool instead of its read list.
		if (this._bulkDeleteKey && (this._bulkDeleteKey === tmpTab))
		{
			this.pict.ContentAssignment.assignContent(pBodySel, this.pict.parseTemplateByHash('ProjectWorkspace-BulkInline', this.workspaceState));
			return;
		}
		const tmpTplHash = TAB_TEMPLATE[tmpTab];
		if (!tmpTplHash) { this.pict.ContentAssignment.assignContent(pBodySel, '<div class="pw-items-note">—</div>'); return; }
		// Line/Bid Items render with a per-list model as Record; the rest read absolute AppData paths (Record ignored).
		let tmpRecord = this.workspaceState;
		if (tmpTab === 'lineitems') { tmpRecord = this._buildItemsModel('lineitems'); }
		else if (tmpTab === 'biditems') { tmpRecord = this._buildItemsModel('biditems'); }
		this.pict.ContentAssignment.assignContent(pBodySel, this.pict.parseTemplateByHash(tmpTplHash, tmpRecord));
	}

	/** OnEnter for every tab (runs after the body is painted): mount the tab's map / association editor / embeds
	 *  and fire its lazy loads. Only the active tab's hosts are in the DOM, so exactly one widget mounts. */
	_mountTab()
	{
		if (!this.idProject) { return; }
		const tmpCA = this.pict.ContentAssignment;
		// Lazy loads for the active tab (were in the old setTab).
		if ((this._activeTab === 'lineitems') || (this._activeTab === 'biditems')) { this._loadItemsAsync(this._activeTab); }
		// (Data ▸ Documents/Forms now embeds the full DocumentList screen — see below — so no built-in mini-list load.)
		// Inline bulk-delete tool.
		if (tmpCA.getElement('#PW-BulkInlineHost').length > 0)
		{
			const tmpBulk = this.pict.providers.ProjectBulkDelete;
			if (tmpBulk && this._bulkSpec && (typeof tmpBulk.openFor === 'function')) { tmpBulk.openFor(this._bulkSpec); }
		}
		for (let i = 0; i < ASSOC_TABS.length; i++)
		{
			if (tmpCA.getElement(ASSOC_TABS[i].Host).length > 0) { this._renderAssoc(ASSOC_TABS[i]); }
		}
		if (tmpCA.getElement('#PW-MapHost').length > 0)
		{
			const tmpMap = this.pict.views.ProjectCenterMap;
			if (tmpMap && (typeof tmpMap.showForProject === 'function')) { tmpMap.showForProject(); }
		}
		if (tmpCA.getElement('#PW-CustomPropsHost').length > 0) { this._renderCustomProps(); }
		// Provider-hosted embeds (openInto('Project', id, host)) — mounted only when their host is on screen.
		if (tmpCA.getElement('#PW-DatesHost').length > 0) { const p = this.pict.providers.RecordDates; if (p && (typeof p.openInto === 'function')) { p.openInto('Project', this.idProject, '#PW-DatesHost'); } }
		if (tmpCA.getElement('#PW-StaffingHost').length > 0) { const p = this.pict.providers.ProjectStaffing; if (p && (typeof p.openInto === 'function')) { p.openInto('Project', this.idProject, '#PW-StaffingHost'); } }
		if (tmpCA.getElement('#PW-EquipmentContractorHost').length > 0) { const p = this.pict.providers.ProjectContractorMatrixEquipment; if (p && (typeof p.openInto === 'function')) { p.openInto('Project', this.idProject, '#PW-EquipmentContractorHost'); } }
		if (tmpCA.getElement('#PW-LineContractorHost').length > 0) { const p = this.pict.providers.ProjectContractorMatrixLine; if (p && (typeof p.openInto === 'function')) { p.openInto('Project', this.idProject, '#PW-LineContractorHost'); } }
		if (tmpCA.getElement('#PW-BidContractorHost').length > 0) { const p = this.pict.providers.ProjectContractorMatrixBid; if (p && (typeof p.openInto === 'function')) { p.openInto('Project', this.idProject, '#PW-BidContractorHost'); } }
		if (tmpCA.getElement('#PW-BidItemMapHost').length > 0) { const p = this.pict.providers.LineItemBidItemMap; if (p && (typeof p.openInto === 'function')) { p.openInto(this.idProject, '#PW-BidItemMapHost'); } }
		// Materials ▸ Sample Plan Dashboard: the shareable SamplePlanDashboard provider painted into this tab's host.
		if (tmpCA.getElement('#PW-SamplePlanDashboardHost').length > 0) { const p = this.pict.providers.SamplePlanDashboard; if (p && (typeof p.openInto === 'function')) { p.openInto(this.idProject, '#PW-SamplePlanDashboardHost'); } }
		// View-hosted embeds (openInto(id, host)) — mounted when on screen, exited (standalone) when not.
		this._mountEmbedView('SamplePlan', '#PW-SamplePlanHost');
		this._mountEmbedView('ProjectFunding', '#PW-FundingHost');
		this._mountEmbedView('ChangeOrders', '#PW-ChangeOrdersHost');
		this._mountEmbedView('ImportantFiles', '#PW-FilesHost');
		this._mountEmbedView('BulkUpload', '#PW-BulkUploadHost');
		this._mountEmbedView('ProjectActivity', '#PW-DataVolumeHost');
		// Data ▸ Observations: the full unified Observations screen (List / Charts / Map), embedded + locked to this
		// project (Project filter hidden). Replaces the old built-in mini-list wholesale.
		if (tmpCA.getElement('#PW-DataObsHost').length > 0) { const p = this.pict.providers.ObservationList; if (p && (typeof p.openInto === 'function')) { p.openInto(this.idProject, '#PW-DataObsHost'); } }
		// Data ▸ Documents/Forms: the full unified Documents screen (List + Charts), embedded + locked to this project.
		if (tmpCA.getElement('#PW-DataFormsHost').length > 0) { const p = this.pict.providers.DocumentList; if (p && (typeof p.openInto === 'function')) { p.openInto(this.idProject, '#PW-DataFormsHost'); } }
	}

	/** Mount a full view into the tab host (openInto) when present; exit its embed (back to standalone) otherwise. */
	_mountEmbedView(pViewName, pHostSel)
	{
		const tmpView = this.pict.views[pViewName];
		if (!tmpView) { return; }
		if (this.pict.ContentAssignment.getElement(pHostSel).length > 0)
		{
			if (typeof tmpView.openInto === 'function') { tmpView.openInto(this.idProject, pHostSel); }
		}
		else if (typeof tmpView.exitEmbed === 'function') { tmpView.exitEmbed(); }
	}

	// --- Data lists (Observations / Forms) — ported from the old view -----------------------------------------

	_dataHostSelector(pKind) { return (pKind === 'data-forms') ? '#PW-DataFormsHost' : '#PW-DataObsHost'; }
	_dataResultsSelector(pKind) { return (pKind === 'data-forms') ? '#PW-DataFormsResults' : '#PW-DataObsResults'; }

	renderDataList(pKind)
	{
		const tmpHost = this._dataHostSelector(pKind);
		if (this.pict.ContentAssignment.getElement(tmpHost).length === 0) { return; }
		const tmpState = this.dataList(pKind) || {};
		const tmpIsForms = (pKind === 'data-forms');
		const tmpNoun = tmpIsForms ? 'forms' : 'observations';
		const tmpResultsId = this._dataResultsSelector(pKind).slice(1);
		this.pict.ContentAssignment.assignContent(tmpHost,
			`<div class="pw-data">`
			+ `<div class="pw-data-toolbar">`
			+ `<input class="pw-data-search" type="text" autocomplete="off" placeholder="Search ${tmpNoun} by name…" value="${this._esc(tmpState.Term || '')}" oninput="_Pict.providers.ProjectWorkspace.onDataSearch('${pKind}', this.value)" />`
			+ `<span class="pw-data-count" id="${tmpResultsId}-count"></span>`
			+ `</div>`
			+ `<div id="${tmpResultsId}" class="pw-data-results"></div>`
			+ `</div>`);
		this.paintDataResults(pKind);
	}

	paintDataResults(pKind)
	{
		const tmpResults = this._dataResultsSelector(pKind);
		if (this.pict.ContentAssignment.getElement(tmpResults).length === 0) { return; }
		const tmpState = this.dataList(pKind) || {};
		const tmpCountEl = document.getElementById(tmpResults.slice(1) + '-count');
		const tmpPageSize = this.dataPageSize;
		const tmpRows = tmpState.Rows || [];
		if (tmpState.Loading && !tmpRows.length)
		{
			if (tmpCountEl) { tmpCountEl.textContent = ''; }
			this.pict.ContentAssignment.assignContent(tmpResults, '<div class="pw-data-state"><i class="fas fa-spinner fa-spin"></i> Loading…</div>');
			return;
		}
		if (!tmpRows.length)
		{
			if (tmpCountEl) { tmpCountEl.textContent = ''; }
			this.pict.ContentAssignment.assignContent(tmpResults, `<div class="pw-data-state">${tmpState.Term ? 'No matches.' : 'Nothing here yet.'}</div>`);
			return;
		}
		const tmpTotal = (tmpState.Total != null) ? tmpState.Total : tmpRows.length;
		const tmpStart = (tmpState.Page * tmpPageSize) + 1;
		const tmpEnd = tmpStart + tmpRows.length - 1;
		if (tmpCountEl) { tmpCountEl.textContent = `${tmpStart}–${tmpEnd} of ${tmpTotal}`; }
		const tmpIsForms = (pKind === 'data-forms');
		const tmpHead = tmpIsForms
			? '<tr><th>Form</th><th>Name</th><th>Status</th><th>Date</th></tr>'
			: '<tr><th>Type</th><th>Name</th><th>Status</th><th>Created</th></tr>';
		const tmpBody = tmpRows.map((pRow) => tmpIsForms ? this._dataFormRow(pRow) : this._dataObsRow(pRow)).join('');
		const tmpHasPrev = tmpState.Page > 0;
		const tmpHasNext = tmpEnd < tmpTotal;
		const tmpPager = (tmpHasPrev || tmpHasNext)
			? `<div class="pw-data-pager">`
				+ `<button class="button is-small" ${tmpHasPrev ? '' : 'disabled'} onclick="_Pict.providers.ProjectWorkspace.dataPage('${pKind}', -1)"><span class="icon"><i class="fas fa-chevron-left"></i></span><span>Prev</span></button>`
				+ `<span class="pw-data-pageno">Page ${tmpState.Page + 1}</span>`
				+ `<button class="button is-small" ${tmpHasNext ? '' : 'disabled'} onclick="_Pict.providers.ProjectWorkspace.dataPage('${pKind}', 1)"><span>Next</span><span class="icon"><i class="fas fa-chevron-right"></i></span></button>`
				+ `</div>`
			: '';
		this.pict.ContentAssignment.assignContent(tmpResults,
			`<div class="pw-data-tablewrap"><table class="pw-data-table"><thead>${tmpHead}</thead><tbody>${tmpBody}</tbody></table></div>${tmpPager}`);
	}

	_dataObsRow(pRow)
	{
		return `<tr><td class="pw-data-type">${this._esc(pRow.ObservationType || '—')}</td>`
			+ `<td class="pw-data-name">${this._esc(pRow.Name || pRow.Description || '—')}</td>`
			+ `<td>${this._dataStatusChip(pRow.Status || pRow.StateName)}</td>`
			+ `<td class="pw-data-date">${this._dataDate(pRow.CreateDate)}</td></tr>`;
	}

	_dataFormRow(pRow)
	{
		return `<tr><td class="pw-data-type">${this._esc(pRow.DocumentType || '—')}</td>`
			+ `<td class="pw-data-name">${this._esc(pRow.Name || pRow.Description || '—')}</td>`
			+ `<td>${this._dataStatusChip(pRow.Status || pRow.StateName)}</td>`
			+ `<td class="pw-data-date">${this._dataDate(pRow.DocumentDate || pRow.UpdateDate)}</td></tr>`;
	}

	_dataStatusChip(pStatus)
	{
		const tmpStatus = String(pStatus == null ? '' : pStatus).trim();
		if (!tmpStatus) { return '<span class="pw-data-status">—</span>'; }
		return `<span class="pw-data-status is-set">${this._esc(tmpStatus)}</span>`;
	}

	_dataDate(pValue)
	{
		if (!pValue) { return '—'; }
		const tmpDate = new Date(pValue);
		if (isNaN(tmpDate.getTime())) { return '—'; }
		return tmpDate.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
	}

	onDataSearch(pKind, pTerm)
	{
		this._dataSearchTimers = this._dataSearchTimers || {};
		if (this._dataSearchTimers[pKind]) { clearTimeout(this._dataSearchTimers[pKind]); }
		this._dataSearchTimers[pKind] = setTimeout(() =>
		{
			this.loadDataListAsync(pKind, { Term: pTerm, Page: 0, Quiet: true });
		}, 300);
	}

	dataPage(pKind, pDelta)
	{
		const tmpState = this.dataList(pKind) || {};
		const tmpNext = Math.max(0, (tmpState.Page || 0) + Number(pDelta));
		this.loadDataListAsync(pKind, { Page: tmpNext, Quiet: true });
	}

	// --- Association editors + Custom-properties tree — ported from the old view -------------------------------

	_ensureAssocEditorView(pConfig)
	{
		if (this.pict.views[pConfig.Hash]) { return this.pict.views[pConfig.Hash]; }
		const tmpManager = this.pict.providers.RecordSetAssociationManager;
		if (!tmpManager || !tmpManager.getAssociation(pConfig.Association)) { return null; }
		const tmpLib = pConfig.Lib || libRecordSetAssociationEditor;
		this.pict.addView(pConfig.Hash, Object.assign({}, tmpLib.default_configuration,
			{
				ViewIdentifier: pConfig.Hash,
				AssociationHash: pConfig.Association,
				ThisRecordSet: 'Project',
				DefaultDestinationAddress: pConfig.Host,
				PickerMode: 'single',
			}), tmpLib);
		const tmpView = this.pict.views[pConfig.Hash];
		// For the observation-type association editor, fire a scoped Project config sync after a CONFIRMED add/remove
		// so the field apps pick up the changed set of types. These jobs are expensive, so we fire ONLY when the
		// associated set actually changed: the editor's renderEditor() recomputes this._otherIDs on a successful
		// mutation and is skipped on cancel/no-op/error, so a change in its length is the reliable mutation signal.
		if (pConfig.SyncOnChange && tmpView && !tmpView._configSyncWrapped)
		{
			tmpView._configSyncWrapped = true;
			const tmpProvider = this;
			[ 'addStaged', 'removeItem' ].forEach((pMethodName) =>
			{
				if (typeof tmpView[pMethodName] !== 'function') { return; }
				const fOriginal = tmpView[pMethodName].bind(tmpView);
				tmpView[pMethodName] = async function ()
				{
					const tmpBefore = (tmpView._otherIDs || []).length;
					const tmpResult = await fOriginal.apply(tmpView, arguments);
					const tmpAfter = (tmpView._otherIDs || []).length;
					if ((tmpAfter !== tmpBefore) && tmpProvider.idProject)
					{
						try { libConfigSync.requestScopedSync(tmpProvider.pict, 'Project', Number(tmpProvider.idProject)); }
						catch (pError) { tmpProvider.pict.log.warn(`ProjectWorkspace: scoped sync after association change failed to start: ${pError && (pError.message || pError)}`); }
					}
					return tmpResult;
				};
			});
		}
		return tmpView;
	}

	_renderAssoc(pConfig)
	{
		const tmpEditor = this._ensureAssocEditorView(pConfig);
		if (!tmpEditor) { return; }
		tmpEditor.options.ThisID = this.idProject;
		tmpEditor.options.DefaultDestinationAddress = pConfig.Host;
		this._projAssocQueue = (this._projAssocQueue || Promise.resolve())
			.then(() => tmpEditor.renderEditor())
			.catch((pError) => { this.pict.log.warn(`ProjectWorkspace: ${pConfig.Association} association render failed: ${pError && (pError.message || pError)}`); });
	}

	_ensureCustomPropsView()
	{
		const tmpHash = 'PW-CustomPropsEditor';
		if (this.pict.views[tmpHash]) { return this.pict.views[tmpHash]; }
		const libObjectEditor = require('pict-section-objecteditor');
		this.pict.addView(tmpHash, Object.assign({}, libObjectEditor.default_configuration,
			{
				ViewIdentifier: tmpHash,
				AutoRender: false,
				Editable: false,
				InitialExpandDepth: 2,
				ObjectDataAddress: 'AppData.ProjectWorkspace.CustomPropsData',
				Renderables:
				[
					{ RenderableHash: 'ObjectEditor-Container', TemplateHash: 'ObjectEditor-Container-Template', DestinationAddress: '#PW-CustomPropsHost', RenderMethod: 'replace' }
				],
			}), libObjectEditor);
		const tmpView = this.pict.views[tmpHash];
		if (tmpView && typeof tmpView.initialize === 'function') { tmpView.initialize(); }
		return tmpView;
	}

	_renderCustomProps()
	{
		// Guided per-customer metadata form (read-only), with an entitlement-gated raw-JSON advanced toggle. The
		// provider owns the raw object editor too (its advanced/no-manifest fallback), bound to the same CustomPropsData.
		const tmpProvider = this.pict.providers.EntityMetadataForm;
		if (tmpProvider && (typeof tmpProvider.openInto === 'function'))
		{
			tmpProvider.openInto({ entityType: 'Project', idCustomer: Number(this._project && this._project.IDCustomer) || 0, dataAddress: 'AppData.ProjectWorkspace.CustomPropsData', host: '#PW-CustomPropsHost', mode: 'view' });
			return;
		}
		// Fallback: the bare read-only object editor if the metadata provider is unavailable.
		const tmpView = this._ensureCustomPropsView();
		if (!tmpView) { return; }
		tmpView.options.DefaultDestinationAddress = '#PW-CustomPropsHost';
		tmpView.render();
	}

	// --- Targeted light-update seams (never a full render → the live map / editors are preserved) --------------

	syncCenterControls()
	{
		if (this.pict.ContentAssignment.getElement('#PW-LocControls').length === 0) { return; }
		this.pict.ContentAssignment.assignContent('#PW-LocControls', this.pict.parseTemplateByHash('ProjectWorkspace-LocControls', this.workspaceState));
	}

	syncCenterInputs()
	{
		const tmpState = this.workspaceState;
		const tmpLat = this.pict.ContentAssignment.getElement('#PW-LatInput');
		const tmpLng = this.pict.ContentAssignment.getElement('#PW-LngInput');
		if (tmpLat && tmpLat.length > 0) { tmpLat[0].value = tmpState.CenterLatInput || ''; }
		if (tmpLng && tmpLng.length > 0) { tmpLng[0].value = tmpState.CenterLngInput || ''; }
	}

	syncAddressResults()
	{
		if (this.pict.ContentAssignment.getElement('#PW-AddressResults').length === 0) { return; }
		this.pict.ContentAssignment.assignContent('#PW-AddressResults', this.pict.parseTemplateByHash('ProjectWorkspace-AddressResults', this.workspaceState));
	}

	/** Repaint just the observation-label (values) list into #PW-ObsLabelHost; returns false if it isn't mounted. */
	syncObsLabels()
	{
		if (this.pict.ContentAssignment.getElement('#PW-ObsLabelHost').length === 0) { return false; }
		const tmpSlot = (this.workspaceState.ObsLabelStateSlot || [])[0];
		if (!tmpSlot) { return false; }
		this.pict.ContentAssignment.assignContent('#PW-ObsLabelHost', this.pict.parseTemplateByHash('Headlight-ObsLabelManager', tmpSlot));
		return true;
	}

	/** Repaint ONLY the label-behavior host (keeps the <details> open + scroll + focus) — falls back to a full render. */
	_syncLabelBehavior()
	{
		this.deriveRenderState();
		if (this.syncLabelBehavior()) { return; }
		this._render();
	}

	syncLabelBehavior()
	{
		if (this.pict.ContentAssignment.getElement('#PW-LabelBehaviorHost').length === 0) { return false; }
		const tmpSlot = (this.workspaceState.LabelBehaviorStateSlot || [])[0];
		if (!tmpSlot) { return false; }
		this.pict.ContentAssignment.assignContent('#PW-LabelBehaviorHost', this.pict.parseTemplateByHash('Headlight-LabelBehavior', tmpSlot));
		return true;
	}
}

module.exports = ProjectWorkspaceProvider;
